import React, { useCallback, useEffect, useState } from 'react';
import moment from 'moment';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Alert, Box, Button, Card, CardContent, Divider, FormControl, Grid, IconButton,
    InputAdornment, InputLabel, Paper, Select, Snackbar, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, TextField, Tooltip, Typography
} from "@mui/material";
import { Search as IconSearch, Close as IconClose } from "@mui/icons-material";

import MantenimientoForm from "../../components/common/MantenimientoForm";
import DialogoBusqueda from "../../components/common/DialogoBusqueda";
import DialogoCredenciales from "../../components/DialogoCredenciales";
import * as periodoService from "../../services/periodoService";
import comprobanteService from "../../services/comprobanteService";
import { getsTerapiaPeriodoResumenView } from "../../services/terapiaService";
import terapiaPeriodoPagoService from "../../services/terapiaPeriodoPagoService";
import { useMantenimiento } from "../../hooks/useMantenimiento";

// Estados del pago del comprobante (Catalogo 143).
const ESTADO_PAGO_CANCELADO = 145;

// Estados basicos (Catalogo 1: 2 Activo, 4 Anulado).
const ESTADO_ACTIVO = 2;
const ESTADO_ANULADO = 4;

const PaymentEdit = () => {
    const { isReadOnly, setIsReadOnly } = useMantenimiento("terapiaPeriodoPago");
    const { id, numero } = useParams();
    const navigate = useNavigate();

    const idTerapiaPeriodo = parseInt(id || "0");
    const numeroPago = parseInt(numero || "0");

    const nuevo = idTerapiaPeriodo <= 0 || numeroPago <= 0;

    const [entity, setEntity] = useState({
        idTerapiaPeriodo: 0,
        numero: 0,
        participante: "",
        idPersona: 0,
        idTerapeuta: 0,
        terapeuta: "",
        idPeriodo: 0,
        codigoPeriodo: "",
        estadoPeriodo: "",
        idTarifa: 0,
        conceptoTarifa: "",
        montoTarifa: 0,
        idDescuento: 0,
        montoDescuento: null,
        idEstadoDescuento: 0,
        idEstado: ESTADO_ACTIVO,
        estado: "A",
        idComprobante: 0,
        serieNumero: "",
        idEstadoPago: 0,
        estadoPago: "",
        montoTotal: 0,
        montoSaldo: 0,
        saldoDisponible: 0,
        idItem: 0,
        item: "",
        observaciones: "",
    });
    const [idComprobanteOriginal, setIdComprobanteOriginal] = useState(0);

    const [cargando, setCargando] = useState(true);
    const [listPeriodos, setListPeriodos] = useState([]);

    const [openMessage, setOpenMessage] = useState(false);
    const [propsMessage, setPropsMessage] = useState({ severity: "success", message: "" });

    // Autorizacion con credenciales de Administrador o Director para Buscar Comprobante.
    const [pidiendoCredenciales, setPidiendoCredenciales] = useState(false);

    // Lupa de la terapia (periodos de terapia).
    const [buscandoPeriodo, setBuscandoPeriodo] = useState(false);
    const [buscandoPeriodoCargando, setBuscandoPeriodoCargando] = useState(false);
    const [filtrosPeriodo, setFiltrosPeriodo] = useState({ periodo: "", participante: "", terapeuta: "" });
    const [periodos, setPeriodos] = useState([]);
    const [periodoElegido, setPeriodoElegido] = useState(null);

    // Lupa del comprobante (se busca el detalle/item).
    const [buscandoComprobante, setBuscandoComprobante] = useState(false);
    const [buscandoComprobanteCargando, setBuscandoComprobanteCargando] = useState(false);
    const [filtrosComprobante, setFiltrosComprobante] = useState({ serie: "", pagador: "", idEstadoPago: 0 });
    const [items, setItems] = useState([]);
    const [itemElegido, setItemElegido] = useState(null);
    const [detalles, setDetalles] = useState([]);

    const mensaje = (severity, texto) => {
        setPropsMessage({ severity: severity, message: texto });
        setOpenMessage(true);
    };

    const obtenerMontoNetoPeriodo = useCallback((datos) => {
        const montoBase = parseFloat(datos?.montoTarifa) || 0;
        const montoDesc = parseFloat(datos?.montoDescuento) || 0;
        const tieneDesc = (datos?.idDescuento > 0 && montoDesc > 0 && (!datos?.idEstadoDescuento || datos?.idEstadoDescuento === ESTADO_ACTIVO))
            || (montoDesc > 0 && (!datos?.idEstadoDescuento || datos?.idEstadoDescuento === ESTADO_ACTIVO));
        return Math.max(montoBase - (tieneDesc ? montoDesc : 0), 0);
    }, []);

    const fetchDetalles = useCallback(async (idComprobante) => {
        if (!idComprobante || idComprobante <= 0) {
            setDetalles([]);
            return;
        }
        const response = await comprobanteService.getsComprobanteDetalleView(idComprobante)
            .catch((err) => { console.log("Err: ", err); });
        setDetalles(response?.data?.data || []);
    }, []);

    const fetchEntity = useCallback(async () => {
        if (nuevo) {
            setCargando(false);
            return;
        }

        setCargando(true);

        const data = await getsTerapiaPeriodoResumenView({
            idPeriodo: 0, idFilial: 0, idTipoTerapia: 0, participante: "",
            idTerapeuta: 0, terapeuta: "", idEstado: 0, idTerapiaPeriodo: idTerapiaPeriodo,
        }).catch((err) => { console.log("Err: ", err); });

        const periodo = (data || [])[0];

        const pagos = await terapiaPeriodoPagoService.getsPagosDelPeriodo(idTerapiaPeriodo)
            .catch((err) => { console.log("Err: ", err); });

        const pago = (pagos || []).find((x) => x.numero == numeroPago);

        const idComp = pago && pago.idComprobante ? pago.idComprobante : 0;
        setIdComprobanteOriginal(idComp);

        setEntity({
            idTerapiaPeriodo: idTerapiaPeriodo,
            numero: numeroPago,
            participante: periodo ? periodo.participante : (pago ? pago.participante : ""),
            idPersona: periodo ? (periodo.idPersona || 0) : (pago ? (pago.idPersona || 0) : 0),
            idTerapeuta: periodo ? (periodo.idTerapeuta || 0) : (pago ? (pago.idTerapeuta || 0) : 0),
            terapeuta: periodo ? (periodo.terapeuta || "") : (pago ? (pago.terapeuta || "") : ""),
            idPeriodo: periodo ? periodo.idPeriodo : (pago ? pago.idPeriodo : 0),
            codigoPeriodo: periodo ? periodo.codigoPeriodo : (pago ? pago.codigoPeriodo : ""),
            estadoPeriodo: periodo ? periodo.estadoApertura : (pago ? pago.estadoPeriodo : ""),
            idTarifa: periodo ? (periodo.idTarifa || 0) : (pago ? (pago.idTarifa || 0) : 0),
            conceptoTarifa: periodo ? (periodo.concepto || "") : (pago ? (pago.concepto || "") : ""),
            montoTarifa: periodo ? (parseFloat(periodo.montoTarifa) || 0) : (pago ? (parseFloat(pago.montoTarifa) || 0) : 0),
            idDescuento: periodo ? (periodo.idDescuento || 0) : (pago ? (pago.idDescuento || 0) : 0),
            montoDescuento: periodo ? periodo.montoDescuento : (pago ? pago.montoDescuento : null),
            idEstadoDescuento: periodo ? (periodo.idEstadoDescuento || 0) : (pago ? (pago.idEstadoDescuento || 0) : 0),
            idEstado: pago ? (pago.idEstado || ESTADO_ACTIVO) : ESTADO_ACTIVO,
            estado: pago ? (pago.estado || "A") : "A",
            idComprobante: idComp,
            serieNumero: pago && idComp > 0 ? (pago.serieNumero || "") : "",
            idEstadoPago: pago && idComp > 0 ? (pago.idEstadoPago || 0) : 0,
            estadoPago: pago && idComp > 0 ? (pago.estadoPago || "") : "",
            montoTotal: pago && idComp > 0 ? (parseFloat(pago.montoTotal) || 0) : 0,
            montoSaldo: pago && idComp > 0 ? (parseFloat(pago.montoSaldo) || 0) : 0,
            saldoDisponible: pago && idComp > 0 ? (parseFloat(pago.saldoDisponible) || 0) : 0,
            idItem: pago && idComp > 0 ? (pago.idItem || 1) : 0,
            item: pago && idComp > 0 ? (pago.concepto || "") : "",
            observaciones: pago ? (pago.observaciones || "") : "",
        });
        fetchDetalles(idComp);

        setCargando(false);
    }, [idTerapiaPeriodo, numeroPago, nuevo, fetchDetalles]);

    const fetchListPeriodos = useCallback(async () => {
        const data = await periodoService.getsPeriodoView({
            idFilial: 0, idTipoTerapia: 0, idEstadoApertura: 0, mesesHaciaAtras: 0, idEstado: 0,
        }).catch((err) => { console.log("Err: ", err); });

        setListPeriodos(data || []);
    }, []);

    useEffect(() => {
        fetchEntity();
        fetchListPeriodos();
    }, [idTerapiaPeriodo, numeroPago, fetchEntity, fetchListPeriodos]);

    const estaAnulado = !nuevo && entity.idEstado === ESTADO_ANULADO;
    const bloquearCambioComprobante = isReadOnly || estaAnulado || entity.idTerapiaPeriodo <= 0;
    const bloquearGuardado = isReadOnly || estaAnulado;

    // ---------- Lupa de la terapia (periodos de terapia) ----------

    const buscarPeriodos = async () => {
        setBuscandoPeriodoCargando(true);

        const data = await getsTerapiaPeriodoResumenView({
            idPeriodo: filtrosPeriodo.periodo == null || filtrosPeriodo.periodo === "" ? 0 : parseInt(filtrosPeriodo.periodo),
            idFilial: 0, idTipoTerapia: 0,
            participante: filtrosPeriodo.participante == null ? "" : filtrosPeriodo.participante,
            idTerapeuta: 0, terapeuta: filtrosPeriodo.terapeuta == null ? "" : filtrosPeriodo.terapeuta,
            idEstado: 0,
        }).catch((err) => { console.log("Err: ", err); return []; });

        setPeriodos(data || []);
        setBuscandoPeriodoCargando(false);
    };

    const abrirBusquedaPeriodo = () => {
        if (!nuevo || isReadOnly) return;
        setBuscandoPeriodo(true);
        buscarPeriodos();
    };

    const elegirPeriodo = async (periodo) => {
        const pagos = await terapiaPeriodoPagoService.getsPagosDelPeriodo(periodo.id)
            .catch((err) => { console.log("Err: ", err); });

        // Si existe un pago pendiente sin comprobante y activo, lo reutilizamos;
        // de lo contrario se asigna el numero siguiente.
        const pendienteSinComprobante = (pagos || []).find(
            (p) => (!p.idComprobante || p.idComprobante === 0) && p.idEstado !== ESTADO_ANULADO
        );
        const siguiente = pendienteSinComprobante
            ? pendienteSinComprobante.numero
            : (pagos || []).reduce((maximo, pago) => Math.max(maximo, pago.numero), 0) + 1;

        setEntity((prev) => ({
            ...prev,
            idTerapiaPeriodo: periodo.id,
            numero: siguiente,
            participante: periodo.participante || "",
            idPersona: periodo.idPersona || 0,
            idTerapeuta: periodo.idTerapeuta || 0,
            terapeuta: periodo.terapeuta || "",
            idPeriodo: periodo.idPeriodo || 0,
            codigoPeriodo: periodo.codigoPeriodo || "",
            estadoPeriodo: periodo.estadoApertura || "",
            idTarifa: periodo.idTarifa || 0,
            conceptoTarifa: periodo.concepto || "",
            montoTarifa: parseFloat(periodo.montoTarifa) || 0,
            idDescuento: periodo.idDescuento || 0,
            montoDescuento: periodo.montoDescuento,
            idEstadoDescuento: periodo.idEstadoDescuento || 0,
            idComprobante: 0,
            serieNumero: "",
            idEstadoPago: 0,
            estadoPago: "",
            montoTotal: 0,
            montoSaldo: 0,
            saldoDisponible: 0,
            idItem: 0,
            item: "",
        }));
        setIdComprobanteOriginal(0);
        setDetalles([]);
        setBuscandoPeriodo(false);
    };

    // ---------- Lupa del comprobante (con Autorizacion de Administrador o Director) ----------

    const buscarItems = async () => {
        setBuscandoComprobanteCargando(true);

        const resp = await comprobanteService.getsComprobanteResumenViewFilter({
            serie: filtrosComprobante.serie == null ? "" : filtrosComprobante.serie,
            pagador: filtrosComprobante.pagador == null ? "" : filtrosComprobante.pagador,
            idEstadoPago: filtrosComprobante.idEstadoPago || 0,
            idEstado: 2,
            soloConSaldoDisponible: true,
        }).catch((err) => { console.log("Err: ", err); return null; });

        const data = resp?.data?.data || [];

        setItems(data.map((row) => ({
            ...row,
            id: row.idComprobante || row.id,
        })));
        setItemElegido(null);
        setBuscandoComprobanteCargando(false);
    };

    const solicitarAutorizacionBuscarComprobante = () => {
        if (bloquearCambioComprobante) return;
        setPidiendoCredenciales(true);
    };

    const autorizacionConfirmada = () => {
        setPidiendoCredenciales(false);
        setBuscandoComprobante(true);
        buscarItems();
    };

    const elegirItem = async (item) => {
        const compId = item.idComprobante || item.id;
        const compResp = await comprobanteService.getComprobanteView(compId)
            .catch(() => null);
        const comp = compResp?.data?.data;

        const idEstPago = comp ? comp.idEstadoPago : item.idEstadoPago;
        const saldoDispActual = comp != null
            ? (parseFloat(comp.saldoDisponible) || 0)
            : (parseFloat(item.saldoDisponible) || 0);

        // Si el comprobante esta Cancelado o Consolidado, validar que su SaldoDisponible alcance
        // para cubrir el monto neto del periodo de terapia.
        if (idEstPago == ESTADO_PAGO_CANCELADO || idEstPago == 227) {
            const montoPeriodo = obtenerMontoNetoPeriodo(entity);
            const yaAsignadoAEstePago = idComprobanteOriginal > 0 && compId == idComprobanteOriginal;
            const saldoEfectivo = yaAsignadoAEstePago ? (saldoDispActual + montoPeriodo) : saldoDispActual;

            if (saldoEfectivo < montoPeriodo) {
                mensaje(
                    "warning",
                    `El comprobante no tiene suficiente saldo disponible (${saldoEfectivo.toFixed(2)}) para cubrir el monto del periodo de terapia (${montoPeriodo.toFixed(2)}).`
                );
                return;
            }
        }

        setEntity((prev) => ({
            ...prev,
            idComprobante: compId,
            serieNumero: item.serieNumero,
            idEstadoPago: idEstPago || prev.idEstadoPago,
            estadoPago: item.estadoPago,
            montoTotal: comp ? (parseFloat(comp.montoTotal) || 0) : prev.montoTotal,
            montoSaldo: comp ? (parseFloat(comp.montoSaldo) || 0) : prev.montoSaldo,
            saldoDisponible: (idEstPago == ESTADO_PAGO_CANCELADO || idEstPago == 227) ? saldoDispActual : 0,
            idItem: null,
            item: "",
        }));

        setBuscandoComprobante(false);
        setItemElegido(null);
        fetchDetalles(compId);
    };

    const cerrarBusquedaComprobante = () => {
        setBuscandoComprobante(false);
        setItemElegido(null);
    };

    const handleDesvincular = async () => {
        if (!window.confirm("¿Está seguro de desvincular el comprobante asignado a este periodo de terapia?")) return;
        try {
            await terapiaPeriodoPagoService.desvincularComprobante(entity.idTerapiaPeriodo, entity.numero);
            mensaje("success", "Comprobante desvinculado correctamente.");
            setEntity((prev) => ({
                ...prev,
                idComprobante: 0,
                serieNumero: "",
                idItem: 0,
                item: "",
                montoTotal: 0,
                montoSaldo: 0,
                saldoDisponible: 0,
                idEstadoPago: 0,
                estadoPago: "",
            }));
            setDetalles([]);
        } catch (err) {
            mensaje("error", err?.response?.data?.message || err?.message || "No se pudo desvincular el comprobante.");
        }
    };

    // ---------- Guardar ----------

    const guardar = async () => {
        if (entity.idTerapiaPeriodo <= 0) {
            mensaje("error", "Elija la terapia y el periodo del pago.");
            return;
        }

        if (entity.idComprobante <= 0) {
            mensaje("error", "Elija el comprobante que paga el periodo.");
            return;
        }

        if (entity.idEstadoPago == ESTADO_PAGO_CANCELADO) {
            const montoPeriodo = obtenerMontoNetoPeriodo(entity);
            const yaAsignadoAEstePago = idComprobanteOriginal > 0 && entity.idComprobante == idComprobanteOriginal;
            const saldoEfectivo = yaAsignadoAEstePago
                ? ((parseFloat(entity.saldoDisponible) || 0) + montoPeriodo)
                : (parseFloat(entity.saldoDisponible) || 0);

            if (saldoEfectivo < montoPeriodo) {
                mensaje(
                    "warning",
                    `El comprobante no tiene suficiente saldo disponible (${saldoEfectivo.toFixed(2)}) para cubrir el monto del periodo de terapia (${montoPeriodo.toFixed(2)}).`
                );
                return;
            }
        }

        await terapiaPeriodoPagoService.addTerapiaPeriodoPago({
            idTerapiaPeriodo: entity.idTerapiaPeriodo,
            numero: entity.numero,
            idComprobante: entity.idComprobante,
            idItem: entity.idItem,
            observaciones: entity.observaciones == null ? "" : entity.observaciones,
        })
            .then((response) => {
                const res = response?.data?.data;
                if (!res || res <= 0) {
                    mensaje("error", "No se pudo guardar el pago del periodo.");
                    return;
                }
                setPropsMessage({ severity: "success", message: "Se guardo el pago del periodo con exito." });
                setOpenMessage(true);
                setIsReadOnly(true);
                if (nuevo) {
                    navigate("/therapyperiod/pago/edit/" + entity.idTerapiaPeriodo + "/" + entity.numero);
                } else {
                    fetchEntity();
                }
            })
            .catch((err) => {
                console.log("Err: ", err);
                const detalleError = err?.response?.data?.detail;
                mensaje(detalleError ? "warning" : "error", detalleError || "No se pudo guardar el pago del periodo.");
            });
    };

    const columnasPeriodos = [
        { field: 'codigoFilial', headerName: 'Filial', width: 70, sortable: false, headerAlign: 'center', align: 'center' },
        { field: 'participante', headerName: 'Participante', width: 175, sortable: false, headerAlign: 'left' },
        { field: 'terapeuta', headerName: 'Terapeuta', width: 175, sortable: false, headerAlign: 'left' },
        { field: 'codigoPeriodo', headerName: 'Periodo', width: 90, sortable: false, headerAlign: 'center', align: 'center' },
        { field: 'estadoApertura', headerName: 'Apertura', width: 100, sortable: false, headerAlign: 'center', align: 'center' },
        { field: 'serieNumero', headerName: 'Comprobante', width: 120, sortable: false, headerAlign: 'center', align: 'center'
            , valueFormatter: (value) => (value == null || value === "" ? "" : value) },
        { field: 'estadoPago', headerName: 'Estado Pago', width: 120, sortable: false, headerAlign: 'center', align: 'center'
            , valueFormatter: (value) => (value == null || value === "" ? "" : value) },
        { field: 'elegir', headerName: '', width: 90, sortable: false, headerAlign: 'center', align: 'center'
            , renderCell: (cellValues) => (
                <Box sx={{ cursor: 'pointer', textDecoration: 'underline', display: 'flex'
                    , alignItems: 'center', justifyContent: 'center', height: '100%', color: 'primary.main' }}
                    onClick={(event) => { event.stopPropagation(); elegirPeriodo(cellValues.row); }}>
                    {"Elegir"}
                </Box>
            )
        },
    ];

    const columnasItems = [
        { field: 'serieNumero', headerName: 'Comprobante', width: 130, sortable: false, headerAlign: 'center', align: 'center' },
        { field: 'fecha', headerName: 'Fecha', width: 105, type: 'date'
            , valueFormatter: (value) => moment(value).format('DD/MM/YYYY')
            , sortable: false, headerAlign: 'center', align: 'center' },
        { field: 'pagador', headerName: 'Pagador', width: 220, sortable: false, headerAlign: 'left' },
        { field: 'montoTotal', headerName: 'Total', width: 100, sortable: false, headerAlign: 'right', align: 'right'
            , valueFormatter: (value) => (parseFloat(value) || 0).toFixed(2) },
        { field: 'montoSaldo', headerName: 'Saldo', width: 100, sortable: false, headerAlign: 'right', align: 'right'
            , valueFormatter: (value) => (parseFloat(value) || 0).toFixed(2) },
        { field: 'saldoDisponible', headerName: 'Saldo Disp.', width: 110, sortable: false, headerAlign: 'right', align: 'right'
            , renderCell: (cellValues) => (cellValues.row.idEstadoPago == ESTADO_PAGO_CANCELADO || cellValues.row.idEstadoPago == 227)
                ? (parseFloat(cellValues.row.saldoDisponible) || 0).toFixed(2)
                : "" },
        { field: 'estadoPago', headerName: 'Estado Pago', width: 120, sortable: false, headerAlign: 'center', align: 'center' },
    ];

    const totalDetalles = (detalles || []).reduce((s, d) => s + (parseFloat(d.subTotal) || 0), 0);

    return (
        <div>
            <MantenimientoForm
                title={(nuevo ? "Nuevo" : "Editar") + " Pago de Periodo de Terapia"}
                backTo={"/therapyperiod/pago"}
                onSave={guardar}
                disabled={bloquearGuardado}
            >
                <Grid container spacing={2}>
                    {estaAnulado && (
                        <Grid item xs={12}>
                            <Alert severity="warning">
                                {"Este pago de periodo se encuentra ANULADO."}
                            </Alert>
                        </Grid>
                    )}

                    {/* Tarjeta 1: Datos del Periodo de Terapia */}
                    <Grid item xs={12}>
                        <Card>
                            <CardContent sx={{ padding: "14px 16px !important" }}>
                                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                        {"Datos del Periodo de Terapia"}
                                    </Typography>
                                </Box>
                                <Divider sx={{ mb: 2 }} />

                                <Grid container spacing={2} alignItems="center">
                                    <Grid item xs={6} sm={2}>
                                        <TextField
                                            disabled
                                            name="numero"
                                            label="N° Pago"
                                            value={entity.numero > 0 ? String(entity.numero).padStart(3, "0") : "000"}
                                            fullWidth
                                            size="small"
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <TextField
                                            disabled
                                            name="participante"
                                            label="Terapia (Participante)"
                                            value={entity.participante}
                                            fullWidth
                                            size="small"
                                            InputLabelProps={{ shrink: true }}
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <Tooltip title={nuevo ? "Buscar terapia y periodo" : "La terapia y periodo no se cambian en un pago existente"}>
                                                            <span>
                                                                <IconButton
                                                                    aria-label="buscar terapia"
                                                                    size="small"
                                                                    edge="end"
                                                                    disabled={!nuevo || isReadOnly}
                                                                    onClick={abrirBusquedaPeriodo}
                                                                >
                                                                    <IconSearch />
                                                                </IconButton>
                                                            </span>
                                                        </Tooltip>
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={6} sm={2}>
                                        <TextField
                                            disabled
                                            name="codigoPeriodo"
                                            label="Periodo"
                                            value={entity.codigoPeriodo}
                                            fullWidth
                                            size="small"
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    </Grid>
                                    <Grid item xs={6} sm={4}>
                                        <TextField
                                            disabled
                                            name="terapeuta"
                                            label="Terapeuta"
                                            value={entity.terapeuta}
                                            fullWidth
                                            size="small"
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    </Grid>

                                    <Grid item xs={12} sm={5}>
                                        <TextField
                                            disabled
                                            name="conceptoTarifa"
                                            label="Concepto / Tarifa"
                                            value={entity.conceptoTarifa}
                                            fullWidth
                                            size="small"
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    </Grid>
                                    <Grid item xs={4} sm={2}>
                                        <TextField
                                            disabled
                                            name="montoTarifa"
                                            label="Monto"
                                            value={(parseFloat(entity.montoTarifa) || 0).toFixed(2)}
                                            fullWidth
                                            size="small"
                                            InputLabelProps={{ shrink: true }}
                                            inputProps={{ style: { textAlign: 'right' } }}
                                        />
                                    </Grid>
                                    <Grid item xs={4} sm={2}>
                                        <TextField
                                            disabled
                                            name="montoDescuento"
                                            label="Descuento"
                                            value={entity.montoDescuento == null ? "" : (parseFloat(entity.montoDescuento) || 0).toFixed(2)}
                                            fullWidth
                                            size="small"
                                            InputLabelProps={{ shrink: true }}
                                            inputProps={{ style: { textAlign: 'right' } }}
                                        />
                                    </Grid>
                                    <Grid item xs={4} sm={3}>
                                        <TextField
                                            disabled
                                            name="estadoPagoPeriodo"
                                            label="Estado Registro"
                                            value={entity.idEstado === ESTADO_ANULADO ? "Anulado" : "Activo"}
                                            fullWidth
                                            size="small"
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Tarjeta 2: Comprobante Referenciado y Detalle */}
                    <Grid item xs={12}>
                        <Card>
                            <CardContent sx={{ padding: "14px 16px !important" }}>
                                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                        {"Comprobante Referenciado"}
                                    </Typography>
                                </Box>
                                <Divider sx={{ mb: 2 }} />

                                <Grid container spacing={2} alignItems="center">
                                    <Grid item xs={12} sm={3}>
                                        <TextField
                                            disabled
                                            name="serieNumero"
                                            label="Comprobante"
                                            value={entity.serieNumero}
                                            fullWidth
                                            size="small"
                                            InputLabelProps={{ shrink: true }}
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <Tooltip title={"Buscar comprobante (requiere autorizacion)"}>
                                                            <span>
                                                                <IconButton
                                                                    aria-label="buscar comprobante"
                                                                    size="small"
                                                                    edge="end"
                                                                    disabled={bloquearCambioComprobante}
                                                                    onClick={solicitarAutorizacionBuscarComprobante}
                                                                >
                                                                    <IconSearch />
                                                                </IconButton>
                                                            </span>
                                                        </Tooltip>
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={3}>
                                        <TextField
                                            disabled
                                            name="montoTotal"
                                            label="Total Comprobante"
                                            value={entity.idComprobante > 0 ? (parseFloat(entity.montoTotal) || 0).toFixed(2) : ""}
                                            fullWidth
                                            size="small"
                                            InputLabelProps={{ shrink: true }}
                                            inputProps={{ style: { textAlign: 'right' } }}
                                        />
                                    </Grid>
                                    <Grid item xs={6} sm={2}>
                                        <TextField
                                            disabled
                                            name="montoSaldo"
                                            label="Saldo"
                                            value={entity.idComprobante > 0 ? (parseFloat(entity.montoSaldo) || 0).toFixed(2) : ""}
                                            fullWidth
                                            size="small"
                                            InputLabelProps={{ shrink: true }}
                                            inputProps={{ style: { textAlign: 'right' } }}
                                        />
                                    </Grid>
                                    <Grid item xs={6} sm={2}>
                                        <TextField
                                            disabled
                                            name="saldoDisponible"
                                            label="Saldo Disponible"
                                            value={entity.idComprobante > 0 && entity.idEstadoPago == ESTADO_PAGO_CANCELADO
                                                ? (parseFloat(entity.saldoDisponible) || 0).toFixed(2)
                                                : ""}
                                            fullWidth
                                            size="small"
                                            InputLabelProps={{ shrink: true }}
                                            inputProps={{ style: { textAlign: 'right' } }}
                                        />
                                    </Grid>
                                    <Grid item xs={6} sm={2}>
                                        <TextField
                                            disabled
                                            name="estadoPago"
                                            label="Estado Pago"
                                            value={entity.estadoPago}
                                            fullWidth
                                            size="small"
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    </Grid>

                                    <Grid item xs={12}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                                            {"Detalle del Comprobante"}
                                        </Typography>
                                        <TableContainer component={Paper} variant="outlined">
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow sx={{ backgroundColor: "action.hover" }}>
                                                        <TableCell width={70} align="center" sx={{ fontWeight: 'bold' }}>{"Item"}</TableCell>
                                                        <TableCell width={90} align="center" sx={{ fontWeight: 'bold' }}>{"Cantidad"}</TableCell>
                                                        <TableCell sx={{ fontWeight: 'bold' }}>{"Concepto de Cobro"}</TableCell>
                                                        <TableCell width={130} align="right" sx={{ fontWeight: 'bold' }}>{"P. Unitario"}</TableCell>
                                                        <TableCell width={130} align="right" sx={{ fontWeight: 'bold' }}>{"Sub Total"}</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {(detalles || []).length === 0 ? (
                                                        <TableRow>
                                                            <TableCell colSpan={5} align="center" sx={{ color: "text.secondary", py: 2 }}>
                                                                {"Sin comprobante referenciado. Use la lupa para buscar y asociar un comprobante."}
                                                            </TableCell>
                                                        </TableRow>
                                                    ) : (
                                                        (detalles || []).map((detalle, index) => (
                                                            <TableRow key={detalle.numero || index}>
                                                                <TableCell align="center">{detalle.numero || (index + 1)}</TableCell>
                                                                <TableCell align="center">{detalle.cantidad || 1}</TableCell>
                                                                <TableCell>{detalle.conceptoCobro}</TableCell>
                                                                <TableCell align="right">
                                                                    {(parseFloat(detalle.precioUnitario ?? detalle.subTotal) || 0).toFixed(2)}
                                                                </TableCell>
                                                                <TableCell align="right">
                                                                    {(parseFloat(detalle.subTotal) || 0).toFixed(2)}
                                                                </TableCell>
                                                            </TableRow>
                                                        ))
                                                    )}
                                                    {(detalles || []).length > 0 && (
                                                        <TableRow sx={{ backgroundColor: "action.hover" }}>
                                                            <TableCell colSpan={4} align="right" sx={{ fontWeight: 'bold' }}>
                                                                {"Total Comprobante:"}
                                                            </TableCell>
                                                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                                                                {totalDetalles.toFixed(2)}
                                                            </TableCell>
                                                        </TableRow>
                                                    )}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </Grid>

                                    <Grid item xs={12}>
                                        <TextField
                                            name="observaciones"
                                            label="Observaciones"
                                            value={entity.observaciones}
                                            fullWidth
                                            multiline
                                            minRows={2}
                                            disabled={bloquearGuardado}
                                            onChange={(event) => setEntity({ ...entity, observaciones: event.target.value })}
                                            size="small"
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </MantenimientoForm>

            {/* Autorizacion con credenciales de Administrador o Director para Buscar Comprobante. */}
            <DialogoCredenciales
                abierto={pidiendoCredenciales}
                accion="buscarComprobante"
                acceso="terapiaPeriodoPago.comprobante"
                titulo="Autorizacion para Buscar Comprobante"
                mensaje="Ingrese las credenciales de un usuario Administrador o Director para buscar un comprobante."
                onCancelar={() => setPidiendoCredenciales(false)}
                onConfirmar={autorizacionConfirmada}
            />

            {/* Lupa de la terapia: busca el periodo de terapia del pago. */}
            <DialogoBusqueda
                open={buscandoPeriodo}
                onClose={() => setBuscandoPeriodo(false)}
                titulo="Buscar Terapia y Periodo"
                columns={columnasPeriodos}
                rows={periodos}
                loading={buscandoPeriodoCargando}
                onSelect={setPeriodoElegido}
                onAceptar={elegirPeriodo}
                filtros={
                    <Grid container spacing={1} alignItems={"center"}>
                        <Grid item xs={6} sm={2}>
                            <FormControl fullWidth>
                                <InputLabel htmlFor="input-periodobusqueda">Periodo</InputLabel>
                                <Select
                                    native
                                    label="Periodo"
                                    value={filtrosPeriodo.periodo}
                                    onChange={(event) => setFiltrosPeriodo({ ...filtrosPeriodo, periodo: event.target.value })}
                                    inputProps={{ name: 'idPeriodo', id: 'input-periodobusqueda' }}
                                    size="small"
                                >
                                    <option key={0} value={0}>{"(Todos)"}</option>
                                    {listPeriodos.map((row) => (
                                        <option key={row.id} value={row.id}>{row.codigo}</option>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={8} sm={3}>
                            <TextField name="participante" label="Participante" value={filtrosPeriodo.participante} fullWidth
                                onChange={(event) => setFiltrosPeriodo({ ...filtrosPeriodo, participante: event.target.value })}
                                onKeyDown={(event) => { if (event.key === "Enter") { event.preventDefault(); buscarPeriodos(); } }}
                                size="small" />
                        </Grid>
                        <Grid item xs={8} sm={3}>
                            <TextField name="terapeuta" label="Terapeuta" value={filtrosPeriodo.terapeuta} fullWidth
                                onChange={(event) => setFiltrosPeriodo({ ...filtrosPeriodo, terapeuta: event.target.value })}
                                onKeyDown={(event) => { if (event.key === "Enter") { event.preventDefault(); buscarPeriodos(); } }}
                                size="small" />
                        </Grid>
                        <Grid item xs={4} sm={1} display={"flex"} alignItems={"center"}>
                            <Tooltip title={"Buscar periodos de terapia"}>
                                <Button variant="outlined" size="medium" fullWidth onClick={() => { buscarPeriodos(); }}>
                                    <IconSearch />
                                </Button>
                            </Tooltip>
                        </Grid>
                    </Grid>
                }
            />

            {/* Lupa del comprobante */}
            <DialogoBusqueda
                open={buscandoComprobante}
                onClose={cerrarBusquedaComprobante}
                titulo="Buscar Comprobante con Saldo Disponible"
                columns={columnasItems}
                rows={items}
                loading={buscandoComprobanteCargando}
                onSelect={setItemElegido}
                onAceptar={elegirItem}
                rowKey={(row) => row.id || row.idComprobante}
                filtros={
                    <Grid container spacing={1} alignItems={"center"}>
                        <Grid item xs={4} sm={3}>
                            <TextField name="serie" label="Serie-Numero" value={filtrosComprobante.serie} fullWidth
                                onChange={(event) => setFiltrosComprobante({ ...filtrosComprobante, serie: event.target.value })}
                                onKeyDown={(event) => { if (event.key === "Enter") { event.preventDefault(); buscarItems(); } }}
                                size="small" />
                        </Grid>
                        <Grid item xs={8} sm={5}>
                            <TextField name="pagador" label="Pagador" value={filtrosComprobante.pagador} fullWidth
                                onChange={(event) => setFiltrosComprobante({ ...filtrosComprobante, pagador: event.target.value })}
                                onKeyDown={(event) => { if (event.key === "Enter") { event.preventDefault(); buscarItems(); } }}
                                size="small" />
                        </Grid>
                        <Grid item xs={4} sm={2} display={"flex"} alignItems={"center"}>
                            <Tooltip title={"Buscar comprobantes"}>
                                <Button variant="outlined" size="medium" fullWidth onClick={() => { buscarItems(); }}>
                                    <IconSearch />
                                </Button>
                            </Tooltip>
                        </Grid>
                    </Grid>
                }
            />

            <Snackbar open={openMessage} autoHideDuration={5000} onClose={() => setOpenMessage(false)}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}>
                <Alert onClose={() => setOpenMessage(false)} severity={propsMessage.severity}>
                    {propsMessage.message}
                </Alert>
            </Snackbar>
        </div>
    );
};

export default PaymentEdit;
